import React, { useState, useEffect } from 'react';
import { TextField } from '@mui/material';
import { Translate } from 'react-localize-redux';
import Modal from '../../../general/modal';

/**
 * SHER-223 Phase 2: form for non-authors to attach a free-text correction
 * (addendum) to an existing submission. The original note is not modified;
 * the addendum is rendered inline below the original by AddendaList.
 *
 * Reused via the same Modal + TextField pattern as the edit-reason modal in
 * ShowSingleSubmissionPatient so the visual language stays consistent.
 */

interface Props {
    open: boolean;
    onClose: () => void;
    onSubmit: (body: string) => Promise<void> | void;
}

export const AddAddendumModal: React.FC<Props> = ({ open, onClose, onSubmit }) => {
    const [body, setBody] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) {
            setBody('');
            setError('');
            setSubmitting(false);
        }
    }, [open]);

    const handleConfirm = async () => {
        if (!body.trim()) {
            setError('required');
            return;
        }
        setError('');
        setSubmitting(true);
        try {
            await onSubmit(body.trim());
            onClose();
        } catch {
            // Action layer dispatches the error to redux state; we keep the
            // modal open so the user can retry without retyping.
            setSubmitting(false);
        }
    };

    return (
        <Modal
            open={open}
            closeModal={submitting ? undefined : onClose}
            title={<Translate id="hospital.correction-form-title" />}
            confirmAction={submitting ? undefined : handleConfirm}
            confirmButtonLabel="general.continue"
        >
            <div data-testid="add-addendum-modal" style={{ width: '100%', maxWidth: 600 }}>
                <TextField
                    label={<Translate id="hospital.correction-note-label" />}
                    placeholder={'Add a correction note…'}
                    value={body}
                    onChange={(e) => { setBody(e.target.value); if (error) setError(''); }}
                    fullWidth
                    multiline
                    minRows={4}
                    required
                    disabled={submitting}
                    error={!!error}
                    helperText={error
                        ? <Translate id="hospital.correction-note-required" />
                        : <Translate id="hospital.correction-note-placeholder" />}
                />
            </div>
        </Modal>
    );
};

export default AddAddendumModal;
